import React from 'react';
import { useFreeTrialContext } from '@/contexts/FreeTrialContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/I18nContext';
import { useBetaMode } from '@/hooks/useBetaMode';
import { router, AppRoute } from '@/lib/router';
import { Badge } from '@/components/ui/badge';
import { Clock, Zap, User, AlertTriangle, ExternalLink } from 'lucide-react';

export const FreeTrialBanner: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { freeTrialStatus, hasTrialStatus, error } = useFreeTrialContext();
  const { betaMode, waitlistUrl, loading: betaLoading } = useBetaMode();

  // 如果已登录，不显示免费试用横幅
  if (isAuthenticated) {
    return null;
  }

  // 如果正在加载内测状态，显示加载状态
  if (betaLoading) {
    return null; // 或者显示一个简单的加载指示器
  }

  // 内测模式下显示 waitlist 横幅
  if (betaMode) {
    return (
      <div className="bg-gradient-to-r from-purple-500 to-purple-700 text-white">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
          {/* 🔑 移动端紧凑布局 - 只显示内测阶段中和加入waitlist按钮 */}
          <div className="flex items-center justify-between gap-2 sm:hidden">
            <span className="text-sm font-medium">{t('freeTrial.betaInProgress')}</span>
            <button
              onClick={() => {
                if (waitlistUrl) {
                  window.open(waitlistUrl, '_blank');
                } else {
                  console.warn('Waitlist URL not configured');
                }
              }}
              className="flex items-center gap-1 px-3 py-1 bg-white text-purple-600 rounded-md text-xs font-medium hover:bg-gray-100 transition-colors h-7"
            >
              <ExternalLink className="w-3 h-3" />
              {t('freeTrial.joinWaitlist')}
            </button>
          </div>

          {/* 🔑 桌面端完整布局 */}
          <div className="hidden sm:flex sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="bg-white text-purple-600">
                <Zap className="w-3 h-3 mr-1" />
                {t('freeTrial.betaStage')}
              </Badge>

              <div className="flex flex-col gap-1 text-sm">
                <div className="font-medium">{t('freeTrial.productInBeta')}</div>
                <div className="text-purple-100 text-xs">
                  {t('freeTrial.welcomeJoinWaitlist')}
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                if (waitlistUrl) {
                  window.open(waitlistUrl, '_blank');
                } else {
                  console.warn('Waitlist URL not configured');
                }
              }}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-white text-purple-600 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              {t('freeTrial.joinWaitlistFull')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 如果没有试用状态且没有错误信息，不显示
  if (!hasTrialStatus && !error) {
    return null;
  }

  const formatExpiryTime = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();

    if (diff <= 0) {
      return t('freeTrial.expired');
    }

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}${t('freeTrial.hours')}${minutes % 60}${t('freeTrial.minutes')}`;
    } else {
      return `${minutes}${t('freeTrial.minutes')}`;
    }
  };

  // 如果有错误信息但没有试用状态，显示错误横幅
  if (error && !freeTrialStatus) {
    return (
      <div className="bg-gradient-to-r from-red-500 to-red-600 text-white">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
          {/* 🔑 移动端紧凑布局 */}
          <div className="flex items-center justify-between gap-2 sm:hidden">
            <span className="text-sm font-medium">{t('freeTrial.freeTrialNotAvailable')}</span>
            <button
              onClick={() => router.navigate(AppRoute.LOGIN)}
              className="flex items-center gap-1 px-3 py-1 bg-white text-red-600 rounded-md text-xs font-medium hover:bg-gray-100 transition-colors h-7"
            >
              {t('auth.login')}
            </button>
          </div>

          {/* 🔑 桌面端完整布局 */}
          <div className="hidden sm:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-1 text-sm">
                <div className="font-medium">{error}</div>
                <div className="text-red-100 text-xs">
                  {error.includes('exhausted') ? t('freeTrial.tokensUsedUpDesc') :
                   error.includes('expired') ? t('freeTrial.trialExpiredDesc') :
                   t('freeTrial.notEligible')}
                </div>
              </div>
            </div>

            <button
              onClick={() => router.navigate(AppRoute.LOGIN)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-white text-red-600 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              <User className="w-4 h-4" />
              {t('freeTrial.registerForMore')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 如果没有试用状态，不显示
  if (!freeTrialStatus) {
    return null;
  }

  // 判断试用状态
  const isTokensExhausted = freeTrialStatus.remainingTokens === 0;
  const isExpired = freeTrialStatus.isExpired || (freeTrialStatus.expiresAt && new Date(freeTrialStatus.expiresAt) <= new Date());
  const isTrialEnded = isTokensExhausted || isExpired || !freeTrialStatus.isActive;

  // 试用已结束状态
  if (isTrialEnded) {
    return (
      <div className="bg-gradient-to-r from-red-500 to-red-600 text-white">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
          {/* 🔑 移动端紧凑布局 */}
          <div className="flex items-center justify-between gap-2 sm:hidden">
            <span className="text-sm font-medium">{t('freeTrial.trialEndedShort')}</span>
            <button
              onClick={() => router.navigate(AppRoute.LOGIN)}
              className="flex items-center gap-1 px-3 py-1 bg-white text-red-600 rounded-md text-xs font-medium hover:bg-gray-100 transition-colors h-7"
            >
              {t('auth.login')}
            </button>
          </div>

          {/* 🔑 桌面端完整布局 */}
          <div className="hidden sm:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="bg-white text-red-600">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {t('freeTrial.trialEndedShort')}
              </Badge>

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <span>{t('freeTrial.remainingTokens')}:</span>
                  <span className="font-bold">0</span>
                </div>

                {freeTrialStatus.expiresAt && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{isExpired ? t('freeTrial.expired') : `${t('freeTrial.remainingTime')}: ${formatExpiryTime(freeTrialStatus.expiresAt)}`}</span>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => router.navigate(AppRoute.LOGIN)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-white text-red-600 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              <User className="w-4 h-4" />
              {t('freeTrial.getOfficialAccount')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 正常试用状态
  return (
    <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white">
      <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
        {/* 🔑 移动端紧凑布局 */}
        <div className="flex items-center justify-between gap-2 sm:hidden">
          <span className="text-sm font-medium">{t('freeTrial.freeTrialActive')}</span>
          <button
            onClick={() => router.navigate(AppRoute.LOGIN)}
            className="flex items-center gap-1 px-3 py-1 bg-white text-blue-600 rounded-md text-xs font-medium hover:bg-gray-100 transition-colors h-7"
          >
            {t('auth.login')}
          </button>
        </div>

        {/* 🔑 桌面端完整布局 */}
        <div className="hidden sm:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="bg-white text-blue-600">
              <Zap className="w-3 h-3 mr-1" />
              {t('freeTrial.title')}
            </Badge>

            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <span>{t('freeTrial.remainingTokens')}:</span>
                <span className="font-bold">{freeTrialStatus.remainingTokens.toLocaleString()}</span>
              </div>

              {freeTrialStatus.expiresAt && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{t('freeTrial.remainingTime')}: {formatExpiryTime(freeTrialStatus.expiresAt)}</span>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => router.navigate(AppRoute.LOGIN)}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
          >
            <User className="w-4 h-4" />
            {t('freeTrial.loginForMore')}
          </button>
        </div>
      </div>
    </div>
  );
}; 